import React from 'react';
import BlogArticleBase from '../BlogArticleBase';
import { zodiacNavItems } from './zodiacNavData';

const PoissonsArticle = () => {
  // Données pour les fils d'Ariane structurés
  const breadcrumbs = [
    {
      name: "Accueil",
      path: "/"
    },
    {
      name: "Blog",
      path: "/blog"
    },
    {
      name: "Signes",
      path: "/blog/signes/"
    },
    {
      name: "Poissons en Astrologie",
      path: "/blog/signes/poissons"
    }
  ];

  // FAQ structurée pour le schema.org FAQPage
  const faqItems = [
    {
      question: "Quelles sont les principales caractéristiques du signe des Poissons?",
      answer: "Les Poissons sont connus pour être intuitifs, empathiques, rêveurs, spirituels et créatifs. Douzième et dernier signe du zodiaque gouverné par Neptune et Jupiter, ils se distinguent par leur sensibilité exceptionnelle, leur compassion et leur connexion au monde invisible."
    },
    {
      question: "Avec quels signes les Poissons sont-ils compatibles?",
      answer: "Les Poissons ont une excellente compatibilité avec le Cancer, le Scorpion, le Taureau et le Capricorne. Leur nature d'eau s'harmonise particulièrement bien avec les autres signes d'eau et de terre."
    },
    {
      question: "Dans quels domaines professionnels les Poissons excellent-ils?",
      answer: "Les Poissons excellent dans les domaines nécessitant intuition, créativité et empathie, comme les arts, la musique, la santé mentale, l'aide sociale, la spiritualité et tous les métiers liés à la guérison, à l'inspiration et à la connexion émotionnelle."
    },
    {
      question: "Quelle est l'influence de Neptune sur les Poissons?",
      answer: "Neptune, planète des rêves et de l'illusion, confère aux Poissons leur intuition profonde, leur imagination fertile et leur sensibilité spirituelle. Elle explique leur perméabilité aux énergies environnantes, leur idéalisme et leur capacité à transcender les limites du monde matériel."
    }
  ];

  // Mots-clés optimisés pour le référencement
  const keywords = [
    "poissons signe astrologique", "caractéristiques poissons", "personnalité poissons", 
    "compatibilité poissons", "poissons et neptune", "horoscope poissons", 
    "poissons élément eau", "carrière poissons", "poissons en amour"
  ];

  return (
    <BlogArticleBase
      title="Poissons : Le Signe du Mystique - Caractéristiques et Compatibilité"
      description="Découvrez tout sur le signe astrologique des Poissons : personnalité, compatibilité amoureuse, carrière et influence de Neptune sur ce signe d'eau intuitif et spirituel."
      slug="signes/poissons"
      image="/images/signes/poissons.webp"
      publishDate="2025-05-18T08:00:00Z"
      modifiedDate="2025-05-18T08:00:00Z"
      author="Valtiel"
      category="Astrologie"
      navItems={zodiacNavItems}
      navTitle="Explorer les signes du zodiaque"
      faqItems={faqItems}
      keywords={keywords}
      breadcrumbs={breadcrumbs}
      showTableOfContents={true}
    >
      {/* TL;DR - Résumé court au début */}
      <div className="article-summary">
        <p><strong>En bref:</strong> Douzième et dernier signe du zodiaque (19 février - 20 mars), les Poissons sont un signe d'eau gouverné par Neptune et Jupiter. Intuitifs, empathiques et rêveurs, les Poissons sont reconnus pour leur sensibilité et leur imagination. Leur nature spirituelle en fait des visionnaires compassionnés et des artistes inspirés.</p>
      </div>

      <h2 id="definition">Le signe des Poissons : dates et caractéristiques essentielles</h2>
      <p>
        Né entre le 19 février et le 20 mars, les Poissons constituent le douzième et dernier signe du zodiaque. 
        Représenté par deux poissons nageant dans des directions opposées, symbolisant la dualité entre réalité et rêve, ce signe d'eau est gouverné par Neptune et traditionnellement par Jupiter.
        Clôturant le cycle zodiacal à la fin de l'hiver et avant l'équinoxe de printemps dans l'hémisphère nord, il symbolise la dissolution des frontières, la transcendance et la connexion universelle.
      </p>
      
      <h2 id="personnalite">Traits de personnalité</h2>
      <p>
        Les Poissons sont connus pour leur nature sensible et leurs caractéristiques distinctives :
      </p>
      <ul>
        <li>Intuitif et perceptif</li>
        <li>Empathique et compatissant</li>
        <li>Rêveur et imaginatif</li>
        <li>Spirituel et mystique</li>
        <li>Créatif et artistique</li>
      </ul>
      
      <p>
        Cependant, ils peuvent aussi présenter certains défis :
      </p>
      <ul>
        <li>Tendance à l'évasion de la réalité</li>
        <li>Parfois confus ou désorganisé</li>
        <li>Peut manquer de limites personnelles</li>
        <li>Vulnérable aux influences extérieures</li>
        <li>Enclin au sacrifice de soi excessif</li>
      </ul>
      
      <h2 id="compatibilite">Compatibilités amoureuses</h2>
      <p>
        Les Poissons étant un signe d'eau, ils trouvent naturellement une harmonie avec les autres signes d'eau ainsi qu'avec les signes de terre.
      </p>
      
      <table>
        <caption>Tableau de compatibilité des Poissons avec les autres signes</caption>
        <thead>
          <tr>
            <th>Compatibilité</th>
            <th>Signes</th>
            <th>Dynamique</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Excellente</td>
            <td>Cancer, Scorpion, Taureau, Capricorne</td>
            <td>Connexion émotionnelle profonde et complémentarité</td>
          </tr>
          <tr>
            <td>Bonne</td>
            <td>Vierge, Poissons</td>
            <td>Équilibre entre rêve et réalité, compréhension mutuelle</td>
          </tr>
          <tr>
            <td>Difficile</td>
            <td>Gémeaux, Sagittaire</td>
            <td>Différences d'approche émotionnelle et d'ancrage</td>
          </tr>
          <tr>
            <td>Défi</td>
            <td>Lion, Verseau</td>
            <td>Divergences fondamentales de perception et de besoins</td>
          </tr>
        </tbody>
      </table>
      
      <h2 id="carriere">Carrière et talents</h2>
      <p>
        Grâce à leur intuition, leur créativité et leur empathie naturelle, les Poissons excellent dans plusieurs domaines :
      </p>
      <ul>
        <li>Arts visuels et musique</li>
        <li>Psychologie et thérapie</li>
        <li>Travail social et humanitaire</li>
        <li>Spiritualité et guidance</li>
        <li>Soins de santé et médecine holistique</li>
      </ul>
      
      <h2 id="planete">Neptune et Jupiter : Planètes gouvernantes</h2>
      <p>
        Neptune, planète des rêves, de l'intuition et de la dissolution des frontières, est la gouvernante moderne des Poissons.
        Jupiter, leur gouvernante traditionnelle, leur apporte expansion et foi.
        Cette double influence explique la nature mystique des Poissons : leur capacité à transcender les limites (Neptune)
        et leur optimisme spirituel, leur générosité d'âme (Jupiter).
      </p>
      
      <h3 id="element">Élément : Eau</h3>
      <p>
        En tant que signe d'eau, les Poissons sont émotionnels, intuitifs et profondément sensibles. 
        L'eau représente le monde des émotions, de l'inconscient et de la connexion invisible. Elle apporte 
        aux Poissons leur fluidité, leur adaptabilité et leur capacité à ressentir ce que les autres ne perçoivent pas.
      </p>

      <h2 id="celebrites">Célébrités Poissons</h2>
      <p>
        De nombreuses personnalités célèbres sont nées sous le signe des Poissons, illustrant les traits caractéristiques de ce signe :
      </p>
      <ul>
        <li>Albert Einstein (14 mars)</li>
        <li>Rihanna (20 février)</li>
        <li>Justin Bieber (1er mars)</li>
        <li>Elizabeth Taylor (27 février)</li>
        <li>Steve Jobs (24 février)</li>
      </ul>

      <h2 id="conseils">Conseils pour les Poissons</h2>
      <p>
        Pour exploiter au mieux les forces des Poissons et atténuer leurs défis potentiels :
      </p>
      <ul>
        <li>Établissez des limites saines tout en restant ouverts aux autres</li>
        <li>Ancrez votre créativité dans des projets concrets</li>
        <li>Pratiquez la méditation pour canaliser votre sensibilité</li>
        <li>Équilibrez votre tendance au sacrifice avec l'auto-soin</li>
        <li>Utilisez votre intuition comme guide sans négliger la réalité pratique</li>
      </ul>

      <blockquote>
        <p>
          "Les Poissons nagent dans un océan sans frontières, là où l'imagination et la réalité se fondent. Leur plus grande sagesse est de comprendre que la compassion sans discernement devient dissolution, mais que la sensibilité guidée par la clarté devient la plus belle forme de magie." — Linda Goodman
        </p>
      </blockquote>

      <h2 id="conclusion">Conclusion</h2>
      <p>
        Les Poissons, avec leur sensibilité et leur intuition, apportent au zodiaque une énergie de transcendance et de connexion universelle. 
        Leur imagination et leur compassion en font un signe qui valorise l'unité spirituelle, 
        la guérison émotionnelle et l'expression artistique inspirée. 
        Comprendre l'influence de Neptune, Jupiter et l'élément Eau sur ce signe permet de mieux apprécier la 
        beauté et la profondeur de la personnalité Poissons, capable de percevoir les dimensions invisibles de l'existence et d'inspirer par leur vision unique.
      </p>
    </BlogArticleBase>
  );
};

export default PoissonsArticle; 